/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from 'react';
import { FormHelperText, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import getRandomId from '../../Utils/getRandomId';
import useErrors from '../../Utils/userErrors';
import useVisibleChipColor from '../../Utils/useVisibleChipColor';
import ErrorText from '../ErrorText';
import withTooltip from '../HOC/withTooltip';
import InputBaseProps from '../InputBase/InputBaseProps';

export interface MultipleSelectProps<T> extends InputBaseProps {
    value: T[];
    onChange: (newValue: T[]) => void;
    optionen: T[];
    render: (option: T) => React.ReactNode;
    onDelete?: (option: T) => void;
    onBlur?: () => void;
}

export default withTooltip(function MultipleSelect<T>(props: MultipleSelectProps<T>) {
    const chipColor = useVisibleChipColor();
    function renderValue(indexe: number[]) {
        if (indexe.length > 0) {
            return (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {getValue(indexe, props.optionen).map((it, index) => {
                        const opts: Partial<ChipProps> = {};
                        if (props.onDelete) {
                            opts.onDelete = () => props.onDelete?.(it);
                        }

                        return (
                            <Chip
                                key={index}
                                label={props.render(it)}
                                color={props.readonly ? "default" : chipColor}
                                variant="outlined"
                                size="small"
                                {...opts}
                                // Wir verhindern hiermit, dass beim Klick auf das Delete-Icon
                                // das Menü mit den Optionen aufgeht.
                                onMouseDown={evt => {
                                    const target = evt.target as Element;
                                    const deleteBtn = evt.currentTarget.lastElementChild;
                                    if (!(target && deleteBtn)) {
                                        return;
                                    }

                                    let stopPropagation = target === deleteBtn;
                                    for (let i = 0; i < deleteBtn.children.length && !stopPropagation; i++) {
                                        stopPropagation = target === deleteBtn.children[i];
                                    }

                                    if (stopPropagation) {
                                        evt.stopPropagation();
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>
            );
        }

        if (props.placeholder) {
            return (
                <Box sx={{ opacity: theme => theme.palette.action.disabledOpacity }}>
                    {props.placeholder}
                </Box>
            );
        }

        return null;
    }

    function getIndexe(value: T[], optionen: T[]) {
        const indexe = [];
        for (const it of value) {
            const index = optionen.indexOf(it);
            if (index !== -1) {
                indexe.push(index);
            }
        }

        return indexe;
    }

    function getValue(indexe: number[], optionen: T[]) {
        const value: T[] = [];
        for (const index of indexe) {
            value.push(optionen[index]);
        }

        return value;
    }

    const { error, errorTexts, clearErrorsOnChange } = useErrors(props);

    const handleChange = clearErrorsOnChange(function (event: SelectChangeEvent<number[]>) {
        const indexe = event.target.value;
        if (typeof indexe === "string") {
            props.onChange(getValue(
                indexe.split(",").map(it => parseInt(it, 10)).filter(it => !isNaN(it)),
                props.optionen));
        } else {
            props.onChange(getValue(indexe, props.optionen));
        }
    });

    const idsRef = React.useRef({
        labelId: getRandomId(),
        id: getRandomId(),
    });

    return (
        <FormControl
            fullWidth
            margin="dense"
            error={error}
            sx={{ mb: 0.5, mt: 1, }}
            id={idsRef.current.labelId}
        >
            <InputLabel
                required={props.required}
                shrink
            >
                {props.caption}
            </InputLabel>
            <Select
                id={idsRef.current.id}
                labelId={idsRef.current.labelId}
                required={props.required}
                disabled={props.readonly}
                autoFocus={props.autoFocus}
                value={getIndexe(props.value, props.optionen)}
                onChange={handleChange}
                renderValue={renderValue}
                onBlur={props.onBlur}
                label={props.caption}
                displayEmpty
                multiple
                notched
            >
                {props.optionen.map((option, index) => {
                    const istAusgewaehlt = props.value.includes(option);
                    return (
                        <MenuItem
                            key={index}
                            value={index}
                            sx={[
                                istAusgewaehlt && {
                                    color: theme => theme.palette.primary.main,
                                    fontWeight: theme => theme.typography.fontWeightMedium,
                                }
                            ]}
                        >
                            {props.render(option)}
                        </MenuItem>
                    );
                })}
            </Select>

            <FormHelperText>
                <ErrorText errors={errorTexts} />
            </FormHelperText>
        </FormControl>
    )
});
